import {
  addEdge,
  Background,
  Connection,
  ConnectionLineType,
  Controls,
  Edge,
  Node,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import { createContext, useCallback, useEffect, useState } from 'react';

import '@xyflow/react/dist/style.css';
import './tree-styling.css';

import assert from 'assert';
import React from 'react';
import { ILegalTransactionRelationship } from '../../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { LegalTransactionRelationshipTypeEnum } from '../../../../../../Interfaces/ILegalTransactionRelationship/LegalTransactionRelationshipTypeEnum';
import {
  legalClientStore,
  useLegalClientStore,
} from '../../../generic/legalClientStore';
import { removeLegalClientsFromRelationshipMembers } from '../../../generic/utilities';
import {
  AddNodeDialog,
  AddNodeDialogConfig,
  AddNodeDialogContext,
} from './AddNodeDialog';
import { layoutElements } from './customLayout';
import CustomNode from './CustomNode';
import { EditLegalClientDialog } from './EditLegalClientDialog';
import {
  pid,
  relationshipToTreeTransformer,
} from './myRelationshipToTreeTransformer';
import {
  InteractionIntent,
  InteractionListener,
  useFamilyTreeInteractionContext,
} from './NewFamilyTreeInteractionContext';
import { Tree, TreeNodeId } from './types';

export const InternalFamilyTreeInteractionContext = createContext<{
  isOpenAddNodeDialog: boolean;
  isOpenEditLegalClientDialog: boolean;
  openAddNodeDialog: (
    parentId: TreeNodeId,
    context: AddNodeDialogContext
  ) => void;
  closeAddNodeDialog: () => void;
  openEditLegalClientDialog: (clientId: TreeNodeId) => void;
  closeEditLegalClientDialog: () => void;
  removeNode: (nodeId: TreeNodeId) => void;
}>({
  isOpenAddNodeDialog: false,
  isOpenEditLegalClientDialog: false,
  openAddNodeDialog: () => {},
  closeAddNodeDialog: () => {},
  openEditLegalClientDialog: () => {},
  closeEditLegalClientDialog: () => {},
  removeNode: () => {},
});

const nodeTypes = {
  custom: CustomNode,
};

export function withReactFlowProvider<
  C extends React.JSXElementConstructor<any>,
>(Component: C) {
  type Props = React.ComponentProps<C>;

  const Wrapped: React.FC<Props> = props => (
    <ReactFlowProvider>
      {React.createElement(Component, props)}
    </ReactFlowProvider>
  );

  Wrapped.displayName = `withReactFlowProvider(${
    (Component as any).displayName || (Component as any).name || 'Component'
  })`;

  return Wrapped;
}

function shouldRelationshipBeKept(
  relationship: ILegalTransactionRelationship
): boolean {
  const isInheritanceRelation =
    relationship.idRelationshipType ===
    LegalTransactionRelationshipTypeEnum.Testament;
  const isMarriageRelation =
    relationship.idRelationshipType ===
    LegalTransactionRelationshipTypeEnum.Marriage;

  const relationshipHasMoreThanOneMember = relationship.Members.length > 1;

  return (
    isInheritanceRelation ||
    (isMarriageRelation && relationshipHasMoreThanOneMember) ||
    (!isInheritanceRelation &&
      !isMarriageRelation &&
      relationshipHasMoreThanOneMember)
  );
}

function removeLegalClientFromTreeAndSyncWithRelationships(
  nodeId: TreeNodeId,
  tree: Tree,
  relationshipArray: ILegalTransactionRelationship[]
): ILegalTransactionRelationship[] {
  const node = tree[nodeId];
  if (!node) return relationshipArray;

  const nodesToRemove = new Set<TreeNodeId>();

  function collectNodesToRemove(currentNodeId: TreeNodeId) {
    if (nodesToRemove.has(currentNodeId)) return;
    nodesToRemove.add(currentNodeId);

    const currentNode = tree[currentNodeId];
    if (!currentNode) return;

    if (currentNode.children && currentNode.isPrimaryLineage) {
      for (const childId of currentNode.children) {
        collectNodesToRemove(childId);
      }
    }
  }

  collectNodesToRemove(nodeId);

  console.log({ nodesToRemove });

  const updatedRelationships: ILegalTransactionRelationship[] = [];
  for (const relationship of relationshipArray) {
    const hasMemberToRemove = relationship.Members.some(member =>
      nodesToRemove.has(pid(member.idLegalClient))
    );

    if (hasMemberToRemove) {
      const updatedRelationship = removeLegalClientsFromRelationshipMembers(
        relationship,
        new Set(Array.from(nodesToRemove).map(id => Number(id)))
      );

      if (shouldRelationshipBeKept(updatedRelationship)) {
        updatedRelationships.push(updatedRelationship);
      }
    } else {
      updatedRelationships.push(relationship);
    }
  }

  return updatedRelationships;
}

export function removeNode(
  tree: Tree,
  relationshipArray: ILegalTransactionRelationship[],
  nodeId: TreeNodeId
): ILegalTransactionRelationship[] {
  const removeLegalClient = legalClientStore.getState().removeLegalClient;

  const node = tree ? tree[nodeId] : null;
  if (node) {
    if (node.kind === 'person') {
      removeLegalClient(Number(nodeId));
      return removeLegalClientFromTreeAndSyncWithRelationships(
        nodeId,
        tree,
        relationshipArray
      );
    } else {
      // relationship node
      // find all persons connected to this relationship
      const connectedPersonIds = node.parents;

      if (!connectedPersonIds) return relationshipArray;

      // for each connected person, remove this relationship from their children
      for (const personId of connectedPersonIds) {
        const personNode = tree[personId];
        if (personNode && personNode.children) {
          personNode.children = personNode.children.filter(
            childId => childId !== nodeId
          );
        }
      }

      // remove the relationship from the relationship array
      const newRelationships = relationshipArray.filter(
        r => r.idRelationship.toString() !== nodeId
      );

      return newRelationships;
    }

    // always remove from relationships
    // if leaf child, do nothing more
    // if relationship node, remove all children, as well as nodes that are not part of the main tree
    // if partner node, remove connected relationship as well as all rules
  }

  return relationshipArray;
}

export const LegalClientNodeContext = {
  Spouse: 'spouse',
  ExSpouse: 'exSpouse',
  Child: 'child',
  Parent: 'parent',
} as const;

export type LegalClientNodeContext =
  (typeof LegalClientNodeContext)[keyof typeof LegalClientNodeContext];

function recalculateTree(
  updatedRelationshipArray: ILegalTransactionRelationship[]
) {
  const tree = relationshipToTreeTransformer(updatedRelationshipArray, '-1');
  const { nodes: layoutedNodes, edges: layoutedEdges } = layoutElements(
    tree,
    '-1'
  );

  return { tree, layoutedNodes, layoutedEdges };
}

function NewFamilyTree({
  relationshipArray,
  setRelationshipArray,
}: {
  relationshipArray: ILegalTransactionRelationship[];
  setRelationshipArray: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationship[]>
  >;
}) {
  const [getLegalClient, legalClientArray] = useLegalClientStore(state => [
    state.getLegalClient,
    state.legalClientArray,
  ]);
  const { register, unregister } = useFamilyTreeInteractionContext();
  const [tree, setTree] = useState<Tree | null>(null);
  const [addNodeDialogConfig, setAddNodeDialogConfig] =
    useState<AddNodeDialogConfig | null>(null);
  const [editLegalClientId, setEditLegalClientId] = useState<number | null>(
    null
  );
  const [legalClientNodeContext, setLegalClientNodeContext] = useState<
    LegalClientNodeContext[]
  >([]);

  const [nodes, setNodes, onNodesChange] = useNodesState(
    [] as Node<Record<string, unknown>, string | undefined>[]
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    [] as Edge<Record<string, unknown>, string | undefined>[]
  );

  const onConnect = useCallback(
    (params: Edge<any> | Connection) =>
      setEdges(eds =>
        addEdge(
          { ...params, type: ConnectionLineType.SmoothStep, animated: true },
          eds
        )
      ),
    []
  );

  const openEditLegalClientDialog = useCallback(
    (clientNodeId: TreeNodeId) => {
      const node = tree?.[clientNodeId];
      const idLegalClient = Number(clientNodeId);
      if (!node || !getLegalClient(idLegalClient)) {
        // TODO: better error handling
        throw new Error(`Legal client with id ${idLegalClient} not found`);
      }

      const newContext: LegalClientNodeContext[] = [];
      if (node.isSpouse) newContext.push(LegalClientNodeContext.Spouse);
      if (node.isExSpouse) newContext.push(LegalClientNodeContext.ExSpouse);
      if (node.children && node.children.length > 0)
        newContext.push(LegalClientNodeContext.Child);
      if (node.parents && node.parents.length > 0)
        newContext.push(LegalClientNodeContext.Parent);
      setLegalClientNodeContext(newContext);

      setEditLegalClientId(idLegalClient);
    },
    [tree, getLegalClient, setLegalClientNodeContext, setEditLegalClientId]
  );

  const closeEditLegalClientDialog = () => {
    setEditLegalClientId(null);
    setLegalClientNodeContext([]);
  };

  const interactionListener: InteractionListener = useCallback(
    (intent, payload) => {
      if (intent === InteractionIntent.OpenEditLegalClientDialog) {
        const { legalClientId } = payload as { legalClientId: number };
        openEditLegalClientDialog(pid(legalClientId));
      }
    },
    [openEditLegalClientDialog]
  );

  useEffect(() => {
    register(interactionListener);
    return () => {
      unregister(interactionListener);
    };
  }, [register, interactionListener]);

  function updateTree(relationshipArray: ILegalTransactionRelationship[]) {
    const {
      tree: newTree,
      layoutedNodes,
      layoutedEdges,
    } = recalculateTree(relationshipArray);
    setTree(newTree);
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }

  useEffect(() => {
    console.log(
      'Relationship array changed, refreshing tree. New relationships:',
      relationshipArray
    );
    updateTree(relationshipArray);
  }, [relationshipArray]);

  useEffect(() => {
    const removedLegalClients = new Set<number>();
    for (const nodeId in tree) {
      const node = tree[nodeId];
      if (node.kind === 'person') {
        const legalClientId = Number(nodeId);
        const legalClient = getLegalClient(legalClientId);
        if (!legalClient) {
          removedLegalClients.add(legalClientId);
        }
      }
    }

    if (tree && removedLegalClients.size > 0) {
      let updatedRelationships = relationshipArray;
      for (const legalClientId of removedLegalClients) {
        updatedRelationships = removeNode(
          tree,
          updatedRelationships,
          pid(legalClientId)
        );
      }
      setRelationshipArray(updatedRelationships);
      updateTree(updatedRelationships);
    }
  }, [legalClientArray]);

  return (
    <div style={{ height: '1080px', border: '1px solid black' }}>
      <InternalFamilyTreeInteractionContext.Provider
        value={{
          isOpenAddNodeDialog: addNodeDialogConfig !== null,
          isOpenEditLegalClientDialog: editLegalClientId !== null,
          openAddNodeDialog: (
            baseNodeId: TreeNodeId,
            context: AddNodeDialogContext
          ) => {
            setAddNodeDialogConfig({ baseNodeId, context });
          },
          closeAddNodeDialog: () => {
            setAddNodeDialogConfig(null);
          },
          openEditLegalClientDialog,
          closeEditLegalClientDialog,
          removeNode: (nodeId: TreeNodeId) => {
            assert(tree, 'Tree must be defined to remove a node');
            const updatedRelationships = removeNode(
              tree,
              relationshipArray,
              nodeId
            );
            setRelationshipArray(updatedRelationships);
            updateTree(updatedRelationships);
          },
        }}
      >
        {addNodeDialogConfig && tree && (
          <AddNodeDialog
            {...addNodeDialogConfig}
            tree={tree}
            relationshipArray={relationshipArray}
            setRelationshipArray={setRelationshipArray}
            isOpen={true}
            onClose={() => {
              setAddNodeDialogConfig(null);
            }}
          />
        )}
        {editLegalClientId && (
          <EditLegalClientDialog
            relationshipArray={relationshipArray}
            legalClientNodeContext={legalClientNodeContext}
            idLegalClient={editLegalClientId}
            isOpen={true}
            onClose={() => {
              setEditLegalClientId(null);
              updateTree(relationshipArray);
            }}
          />
        )}
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          connectionLineType={ConnectionLineType.SmoothStep}
          fitView
          nodeTypes={nodeTypes}
        >
          <Background />
          <Controls />
        </ReactFlow>
      </InternalFamilyTreeInteractionContext.Provider>
    </div>
  );
}

export default withReactFlowProvider(NewFamilyTree);
